// Ported from Stephanet's MissedDaysPayroll.jsx (Missed Days Management).
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {
  AlertTriangle, Banknote, Building2, CheckCircle2, ChevronDown, ChevronUp, ClipboardList, Download, Eye, FileText, Filter, MessageSquare,
  Paperclip, Pen, PiggyBank, Printer, RotateCcw, Send, SquareCheckBig, TrendingDown, TrendingUp, Users, Wallet, Clock, CalendarDays,
} from 'lucide-react'
import * as hr from '../api'
import Loader from '../components/Loader'
import { useHR } from '../HRContext'
import Modal from '../components/Modal'
import { FileChip, FilePicker, openStored } from '../components/Files'
import { CHECKLIST_RULE } from '../fileRules'
import { download, fmtMoney, toCSV } from '../format'
import { DateInput, Select } from '../components/Controls'
import { Chip, Kpi, num } from './missed/shared'

// Payroll Deduction: the deduction register (post declined missed days to payroll) and the Cost to Company report

const VIEWS = ['Payroll Deduction Register', 'Cost to Company Report']
const BAR = ['#0b318b', '#2563eb', '#0891b2', '#7c3aed', '#16a34a', '#d97706', '#dc2626', '#475569']
const SEV_TONE = { Low: 'green', Medium: 'yellow', High: 'orange', Critical: 'darkred' }
const COST_TONE = { Pending: 'yellow', 'Under Review': 'purple', Reviewed: 'green', Escalated: 'orange', Closed: 'gray' }
const APPR_TONE = { Approved: 'green', Declined: 'red', 'Pending Review': 'yellow' }
const PAY_TONE = { 'Not Required': 'gray', 'Pending Posting': 'yellow', 'Posted to Payroll': 'green', Reversed: 'purple', Posted: 'green', Pending: 'yellow',
  'Awaiting Approval': 'orange', 'Awaiting Posting Approval': 'orange' }
const BATCH_TONE = { Pending: 'yellow', Approved: 'green', Rejected: 'red', Returned: 'orange', Cancelled: 'gray' }
const money = v => (v ? fmtMoney(v) : '—')
const monthLabel = m => { const [y, mo] = m.split('-'); return new Date(+y, +mo - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' }) }

export default function MissedDaysPayroll() {
  const [view, setView] = useState(VIEWS[0])
  return (
    <div className="mdr mds mdp">
      <div className="mds-tabs no-print" role="tablist">
        {VIEWS.map(v => <button key={v} type="button" role="tab" className={v === view ? 'active' : ''} onClick={() => setView(v)}>
          {v === VIEWS[0] ? <ClipboardList size={14} /> : <TrendingUp size={14} />} {v}</button>)}
      </div>
      {view === VIEWS[0] ? <Register /> : <CostReport />}
    </div>
  )
}

// ---------------------------------------------------------------- Payroll Deduction Register

function Register() {
  const { notify } = useHR()
  const [month, setMonth] = useState('')
  const [d, setD] = useState(null)
  const [error, setError] = useState('')
  const [confirm, setConfirm] = useState(null) // rows to post
  const [busy, setBusy] = useState(false)

  const load = (m = month) => hr.mdPayroll(m).then(x => { setD(x); setMonth(x.month); setError('') }).catch(e => setError(e.message))
  useEffect(() => { load('') }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !d) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!d) return <Loader title="Loading payroll deductions…" subtitle="Fetching declined missed days" />
  const pending = d.rows.filter(r => r.payroll === 'Pending')
  const total = d.rows.reduce((s, r) => s + r.deduction, 0)

  const post = () => {
    setBusy(true)
    hr.mdPayrollPost(confirm.map(r => r.id))
      .then(r => { notify(`${r.submitted ? r.message : `${r.done} deduction(s) posted to payroll`}${r.skipped.length ? ` · ${r.skipped.length} skipped` : ''}`); setConfirm(null); return load() })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false))
  }
  const exportCsv = () => download(`payroll-deductions-${d.month}.csv`, toCSV([
    { label: 'MD ID', get: r => r.reference }, { label: 'Employee ID', get: r => r.employee_no }, { label: 'Employee Name', get: r => r.name },
    { label: 'Department', get: r => r.department }, { label: 'Date Missed', get: r => r.date }, { label: 'Salary Basis', get: r => r.basis },
    { label: 'Monthly Gross', get: r => r.monthly_gross }, { label: 'Working Days', get: r => r.working_days }, { label: 'Daily Rate', get: r => r.daily_rate },
    { label: 'Deduction Amt', get: r => r.deduction }, { label: 'Status', get: r => r.status }, { label: 'Payroll Status', get: r => r.payroll },
    { label: 'Batch', get: r => r.batch }, { label: 'Posted By', get: r => r.posted_by }, { label: 'Posted At', get: r => r.posted_at },
  ], d.rows))

  return (
    <>
      <div className="mdr-top">
        <div>
          <h2>Payroll Deduction Impact Report</h2>
          <p>Review and post salary deductions from declined missed days to payroll</p>
        </div>
        <div className="mdr-top-actions no-print">
          {d.can.export && <button className="mdr-btn" onClick={exportCsv}><Download size={14} /> Export</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
          {d.can.post && <button className="mdp-post-all" disabled={!pending.length || busy} onClick={() => setConfirm(pending)}><Send size={14} /> {d.posting_approval ? 'Send All for Posting Approval' : 'Post All to Payroll'}</button>}
        </div>
      </div>

      <div className="md-kpis mds-kpis mdp-kpis4">
        <Kpi label="Pending Deductions" value={d.kpis.pending} Ico={Clock} tone="amber" note="Awaiting posting" />
        <Kpi label="Total Pending Amount" value={fmtMoney(d.kpis.pending_amount)} Ico={Wallet} tone="orange" note={d.label} i={1} />
        <Kpi label="Posted to Payroll" value={d.kpis.posted} Ico={CheckCircle2} tone="green" note="Deductions posted" i={2} />
        <Kpi label="Total Posted Amount" value={fmtMoney(d.kpis.posted_amount)} Ico={Banknote} tone="blue" note={d.label} i={3} />
      </div>

      <div className="mdr-card mdp-chart">
        <div className="mds-card-title"><Building2 size={16} /> Deductions by Department</div>
        {d.departments.length ? (
          <ResponsiveContainer width="100%" height={Math.max(140, d.departments.length * 44 + 40)}>
            <BarChart data={d.departments} layout="vertical" margin={{ left: 10, right: 30, top: 6, bottom: 6 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" tickFormatter={v => `₦${Math.round(v / 1000)}k`} fontSize={11} stroke="#8a9aa3" />
              <YAxis type="category" dataKey="name" width={130} fontSize={12} stroke="#475569" />
              <Tooltip formatter={v => [fmtMoney(v), 'Deduction']} cursor={{ fill: 'rgba(11,49,139,.06)' }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>{d.departments.map((x, i) => <Cell key={x.name} fill={BAR[i % BAR.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="mdr-empty">No deductions in {d.label}.</div>}
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head">
          <b>Salary Deduction Register</b>
          <label className="mdp-month no-print">Month:
            <Select value={month} onChange={e => { setMonth(e.target.value); load(e.target.value) }} aria-label="Payroll month">
              {d.months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </Select>
          </label>
          <span className="print-only">Month: {d.label}</span>
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>
              <th>Employee ID</th><th>Employee Name</th><th>Department</th><th>Date Missed</th><th>Salary Basis</th><th className="r">Monthly Gross</th>
              <th className="c">Working Days</th><th className="r">Daily Rate</th><th className="r">Deduction Amt</th><th>Status</th><th>Payroll Status</th><th className="no-print">Action</th>
            </tr></thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 22}ms` }}>
                  <td className="mdr-emp">{r.employee_no}</td><td className="mdr-name">{r.name}</td><td>{r.department}</td>
                  <td className="nowrap">{r.date}</td><td className="nowrap">{r.basis}</td><td className="r nowrap">{r.basis === 'Daily Rate' ? <span className="mds-dash">—</span> : fmtMoney(r.monthly_gross)}</td>
                  <td className="c">{r.working_days}</td><td className="r nowrap">{fmtMoney(r.daily_rate)}</td>
                  <td className="r mdr-ded"><span className="neg">{fmtMoney(r.deduction)}</span></td>
                  <td><Chip tone="red">{r.status}</Chip></td>
                  <td><Chip tone={PAY_TONE[r.payroll]}>{r.payroll}</Chip>{r.batch && <small className="mdp-batch">{r.batch}</small>}</td>
                  <td className="no-print">
                    {r.payroll === 'Pending'
                      ? (d.can.post ? <button className="mdp-post" disabled={busy} onClick={() => setConfirm([r])}><Send size={12} /> {d.posting_approval ? 'Send' : 'Post'}</button> : <span className="mds-dash">—</span>)
                      : r.payroll === 'Awaiting Approval' ? <Chip tone="orange">{`Awaiting · ${r.batch}`}</Chip>
                        : <span className="mdp-done"><CheckCircle2 size={13} /> Posted</span>}
                  </td>
                </tr>
              ))}
              {!d.rows.length && <tr><td colSpan={12} className="mdr-empty">No declined missed days in {d.label}.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mdr-foot">
          <span>Total records: <b>{d.rows.length}</b></span>
          <span>Total Deduction: <strong>{fmtMoney(total)}</strong></span>
        </div>
      </div>

      {(d.posting_approval || d.batches?.length > 0) && (
        <div className="mdr-card md-batches">
          <h4>Posting Approval Batches {d.posting_approval && <small className="muted">— Payroll Posting Approval Required is on: deductions reach payroll once their batch is approved
            {d.kpis.awaiting ? ` (${d.kpis.awaiting} awaiting, ${fmtMoney(d.kpis.awaiting_amount)})` : ''}</small>}</h4>
          <table className="mdr-table">
            <thead><tr><th>Batch</th><th>Status</th><th>Records</th><th>Amount</th><th>Requested By</th><th>Requested</th><th>Note</th></tr></thead>
            <tbody>
              {(d.batches || []).map(b => (
                <tr key={b.id}><td className="mdr-id">{b.reference}</td><td><Chip tone={BATCH_TONE[b.status]}>{b.status}</Chip></td><td>{b.records}</td>
                  <td>{fmtMoney(b.amount)}</td><td>{b.requested_by || '—'}</td><td className="nowrap">{String(b.at || '').replace('T', ' ').slice(0, 16)}</td><td>{b.note || '—'}</td></tr>
              ))}
              {!d.batches?.length && <tr><td colSpan={7} className="mdr-empty">No batches sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <Modal className="mdl-modal" title={<><Send size={18} /> Post to Payroll</>} onClose={() => setConfirm(null)}>
          <div className="mdl-modal-body">
            <p className="mdl-modal-lead">{confirm.length === 1 ? <>Post the deduction for <b>{confirm[0].name}</b> ({confirm[0].date}) to payroll?</> : <>Post <b>{confirm.length}</b> pending deduction(s) for <b>{d.label}</b> to payroll?</>}</p>
            <div className="md-kpis mds-kpis mdl-kpis">
              <Kpi label="Deductions" value={confirm.length} Ico={ClipboardList} tone="amber" note="To be posted" />
              <Kpi label="Total Amount" value={fmtMoney(confirm.reduce((s, r) => s + r.deduction, 0))} Ico={Banknote} tone="rose" note={`Batch ${confirm[0].date.slice(0, 7)}`} i={1} />
            </div>
            <div className="mdl-info"><AlertTriangle size={15} /> {d.posting_approval
              ? 'Payroll Posting Approval Required is on: the deductions go together as one batch to the Missed Day Payroll Posting approval and reach payroll once it is approved.'
              : <>Posted deductions are locked here. To undo one, reverse the missed day from Review &amp; Approval.</>}</div>
            <div className="mdh-form-foot">
              <button type="button" className="mdr-btn" onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className="mds-run" disabled={busy} onClick={post}><Send size={14} /> {busy ? 'Sending…' : d.posting_approval ? 'Send for Posting Approval' : 'Post to Payroll'}</button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

// ---------------------------------------------------------------- Cost to Company Report

const INCIDENTS = ['Missed Day', 'Lateness', 'Leave Offset', 'Excess Leave', 'Biometric Error']
const APPROVALS = ['Approved', 'Declined', 'Pending Review']
const PAYROLLS = ['Not Required', 'Pending Posting', 'Posted to Payroll', 'Reversed']
const CATEGORIES = {
  'Company Paid Cost': r => r.company_paid > 0,
  'Salary Cost Saved by Deduction': r => r.saved > 0,
  'Operational Cost to Company': r => r.operational_cost > 0,
  'Replacement Cost': r => r.operational_cost > 0 && r.impact_type === 'Overtime Replacement',
  'Client Impact Cost': r => r.operational_cost > 0 && ['Client Dissatisfaction', 'Loss of Client'].includes(r.impact_type),
  'Project Delay Cost': r => r.operational_cost > 0 && r.impact_type === 'Project Delay',
}
const FROZEN = 5
const NOFILTER = { q: '', department: '', branch: '', site: '', grade: '', designation: '', incident: '', approval: '', payroll: '', category: '', impact: '', status: '' }

const COLS = [
  ['Date', r => r.date], ['Employee ID', r => r.employee_no], ['Employee Name', r => r.name], ['Dept', r => r.department], ['Designation', r => r.designation],
  ['Grade', r => r.grade], ['Branch', r => r.branch], ['Site', r => r.site], ['Incident Type', r => r.incident], ['Reason', r => r.reason],
  ['Approval', r => r.approval], ['Leave Type', r => r.leave_type], ['Paid?', r => (r.paid ? 'Yes' : 'No')], ['Deducted?', r => (r.deducted ? 'Yes' : 'No')],
  ['Daily Rate', r => r.daily_rate], ['Hourly Rate', r => r.hourly_rate], ['Missed Days', r => r.missed_days], ['Late Mins', r => r.late_mins], ['Late Hrs', r => r.late_hrs],
  ['Company Paid', r => r.company_paid], ['Deduction', r => r.deduction], ['Salary Saved', r => r.saved], ['Operational Cost', r => r.operational_cost],
  ['Net Cost to Co.', r => r.net], ['Impact Type', r => r.impact_type], ['Severity', r => r.severity], ['HR Comment', r => r.hr_comment], ['Mgmt Comment', r => r.mgmt_comment],
  ['Payroll Status', r => r.payroll], ['Action Taken', r => r.action_taken], ['Action By', r => r.action_by], ['Action Date', r => r.action_date], ['Status', r => r.cost_status],
]
const csvCols = COLS.map(([label, get]) => ({ label, get }))

function CostReport() {
  const { notify } = useHR()
  const [period, setPeriod] = useState(null) // null: the server picks the latest month with missed days
  const [f, setF] = useState(NOFILTER)
  const [d, setD] = useState(null)
  const [error, setError] = useState('')
  const [modal, setModal] = useState(null) // {kind, row}
  const [open, setOpen] = useState(true)
  const [printing, setPrinting] = useState(false)

  const load = () => hr.mdCost(period ? { start: period.start, end: period.end } : {})
    .then(x => { setD(x); setError(''); if (!period) setPeriod({ start: x.period.start, end: x.period.end }) }).catch(e => setError(e.message))
  useEffect(() => { if (!period || (period.start && period.end && period.start <= period.end)) load() }, [period?.start, period?.end]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { // printing one row: open its detail, print, then close
    if (!printing) return
    document.body.classList.add('mdp-print-one')
    const t = setTimeout(() => { window.print(); document.body.classList.remove('mdp-print-one'); setPrinting(false) }, 350)
    return () => clearTimeout(t)
  }, [printing])

  const opts = useMemo(() => {
    const u = k => [...new Set((d?.rows || []).map(r => r[k]).filter(Boolean))].sort()
    return { department: u('department'), branch: u('branch'), site: u('site'), grade: u('grade'), designation: u('designation') }
  }, [d])
  const rows = useMemo(() => {
    if (!d) return []
    const s = f.q.trim().toLowerCase()
    return d.rows.filter(r => (!s || [r.name, r.employee_no].some(v => String(v || '').toLowerCase().includes(s)))
      && (!f.department || r.department === f.department) && (!f.branch || r.branch === f.branch) && (!f.site || r.site === f.site)
      && (!f.grade || r.grade === f.grade) && (!f.designation || r.designation === f.designation) && (!f.incident || r.incident === f.incident)
      && (!f.approval || r.approval === f.approval) && (!f.payroll || r.payroll === f.payroll) && (!f.category || CATEGORIES[f.category](r))
      && (!f.impact || r.impact_type === f.impact) && (!f.status || r.cost_status === f.status))
  }, [d, f])

  // the first FROZEN columns (# · Actions · Date · Employee ID · Employee Name) stay put while the table scrolls sideways
  const tableRef = useRef(null)
  useLayoutEffect(() => {
    const t = tableRef.current
    if (!t) return
    const place = () => {
      let left = 0
      ;[...t.querySelectorAll('thead th')].slice(0, FROZEN).forEach((th, i) => { t.style.setProperty(`--fz${i + 1}`, `${left}px`); left += th.getBoundingClientRect().width })
    }
    place()
    const ro = new ResizeObserver(place)
    ro.observe(t)
    return () => ro.disconnect()
  }, [d, rows.length])

  if (error && !d) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!d || !period) return <Loader title="Loading cost to company…" subtitle="Fetching costs and evidence" />

  const sum = (k, list = rows) => list.reduce((s, r) => s + (r[k] || 0), 0)
  const missed = rows.filter(r => r.source === 'missed_day')
  const lates = rows.filter(r => r.source === 'lateness')
  const k = {
    incidents: rows.length, mdCost: sum('company_paid', missed), lateCost: sum('company_paid', lates), paid: sum('company_paid'),
    mdSaved: sum('saved', missed), lateSaved: sum('saved', lates), saved: sum('saved'), op: sum('operational_cost'), net: sum('net'),
    emps: new Set(rows.map(r => r.employee_id)).size, depts: new Set(rows.map(r => r.department)).size,
    high: rows.filter(r => ['High', 'Critical'].includes(r.severity)).length,
  }
  const set = (key, v) => setF(x => ({ ...x, [key]: v }))
  const clear = () => { setF(NOFILTER); setPeriod(null) }
  const periodLabel = `${period.start} to ${period.end}`
  const save = (row, body, msg) => hr.mdCostImpact({ source: row.source, source_id: row.source_id, ...body })
    .then(r => { if (msg !== false) notify(msg || (r.changes ? 'Cost impact saved' : 'No changes')); load(); return r })
  const act = (kind, r) => {
    if (kind === 'reviewed') save(r, { cost_status: 'Reviewed', _action: 'review' }, `${r.name} · ${r.date} marked reviewed`).catch(e => notify(e.message, 'bad'))
    else if (kind === 'export') download(`cost-impact-${r.employee_no}-${r.date}.csv`, toCSV(csvCols, [r]))
    else if (kind === 'print') { setModal({ kind: 'view', row: r }); setPrinting(true) }
    else setModal({ kind, row: r })
  }
  const select = (key, label, list) => (
    <label>{label}
      <Select value={f[key]} onChange={e => set(key, e.target.value)}>
        <option value="">All</option>{list.map(o => <option key={o} value={o}>{o}</option>)}
      </Select>
    </label>
  )

  return (
    <>
      <div className="mdr-top">
        <div>
          <h2>Cost to Company Report</h2>
          <p>Financial impact from approved/declined missed days and lateness — management reporting view</p>
        </div>
        <div className="mdr-top-actions no-print">
          {d.can.export && <button className="mdr-btn" onClick={() => download(`cost-to-company-${period.start}-to-${period.end}.csv`, toCSV(csvCols, rows))}><Download size={14} /> Export CSV</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
        </div>
      </div>

      <div className="mdr-card mds-filters no-print">
        <div className="mdp-filter-head">
          <div className="mds-card-title"><Filter size={15} /> Filter Panel</div>
          <button className="mdr-btn" onClick={clear}><RotateCcw size={13} /> Clear All</button>
        </div>
        <div className="mds-grid">
          <label>From Date<DateInput value={period.start} max={period.end} onChange={e => setPeriod(p => ({ ...p, start: e.target.value }))} /></label>
          <label>To Date<DateInput value={period.end} min={period.start} onChange={e => setPeriod(p => ({ ...p, end: e.target.value }))} /></label>
          {select('department', 'Department', opts.department)}
          {select('branch', 'Branch', opts.branch)}
          {select('site', 'Project Site', opts.site)}
          <label>Employee Search<input placeholder="Name or ID..." value={f.q} onChange={e => set('q', e.target.value)} /></label>
          {select('grade', 'Grade', opts.grade)}
          {select('designation', 'Designation', opts.designation)}
          {select('incident', 'Incident Type', INCIDENTS)}
          {select('approval', 'Approval Status', APPROVALS)}
          {select('payroll', 'Payroll Status', PAYROLLS)}
          {select('category', 'Cost Category', Object.keys(CATEGORIES))}
          {select('impact', 'Management Impact', d.options.impact_types)}
          {select('status', 'Action Status', d.options.statuses)}
        </div>
      </div>

      <div className="md-kpis mds-kpis mdp-kpis">
        {[['Total Incidents', k.incidents, ClipboardList, 'blue', 'In period'],
          ['Approved Missed Day Cost', fmtMoney(k.mdCost), CalendarDays, 'sky', 'Paid by company'],
          ['Approved Lateness Cost', fmtMoney(k.lateCost), Clock, 'cyan', 'Paid late time'],
          ['Total Company Paid Cost', fmtMoney(k.paid), Wallet, 'indigo', 'Approved + Paid by company'],
          ['Missed Day Salary Saved', fmtMoney(k.mdSaved), PiggyBank, 'green', 'Declined & deducted'],
          ['Lateness Salary Saved', fmtMoney(k.lateSaved), TrendingDown, 'emerald', 'Lateness deductions'],
          ['Total Salary Cost Saved', fmtMoney(k.saved), Banknote, 'teal', 'From all deductions'],
          ['Total Operational Cost', fmtMoney(k.op), AlertTriangle, 'orange', 'Manual entries by HR/Mgmt'],
          [`Net Company Impact (${k.net >= 0 ? 'Cost ↑' : 'Saving ↓'})`, fmtMoney(Math.abs(k.net)), k.net >= 0 ? TrendingUp : TrendingDown, k.net >= 0 ? 'rose' : 'green', k.net >= 0 ? 'Net cost to company' : 'Net saving to company'],
          ['Employees Affected', k.emps, Users, 'purple', 'With incidents'],
          ['Departments Affected', k.depts, Building2, 'slate', 'With incidents'],
          ['High Impact Cases', k.high, AlertTriangle, 'red', 'High or Critical severity']]
          .map(([label, value, Ico, tone, note], i) => <Kpi key={label} {...{ label, value, Ico, tone, note, i }} />)}
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head">
          <b>Detailed Cost Register</b>
          <span className="mds-note-inline">{rows.length} record(s) | Period: {periodLabel}</span>
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table mdp-table mdp-frozen" ref={tableRef}>
            <thead><tr>
              <th>#</th><th className="no-print">Actions</th>{COLS.map(([l]) => <th key={l} className={['Daily Rate', 'Hourly Rate', 'Company Paid', 'Deduction', 'Salary Saved', 'Operational Cost', 'Net Cost to Co.'].includes(l) ? 'r' : ''}>{l}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 18}ms` }}>
                  <td>{i + 1}</td>
                  <td className="no-print">
                    <div className="mdr-actions">
                      <button className="mdr-act a-blue" title="View" onClick={() => act('view', r)}><Eye size={12} /></button>
                      {d.can.edit && <>
                        <button className="mdr-act a-indigo" title="Edit Cost Impact" onClick={() => act('edit', r)}><Pen size={12} /></button>
                        <button className="mdr-act a-purple" title="Add Management Comment" onClick={() => act('comment', r)}><MessageSquare size={12} /></button>
                        <button className="mdr-act a-teal" title="Attach Evidence" onClick={() => act('attach', r)}><Paperclip size={12} />{r.evidence > 0 && <sup>{r.evidence}</sup>}</button>
                        <button className="mdr-act a-green" title="Mark Reviewed" disabled={r.cost_status === 'Reviewed'} onClick={() => act('reviewed', r)}><SquareCheckBig size={12} /></button>
                      </>}
                      <button className="mdr-act a-gray" title="Export Row" onClick={() => act('export', r)}><Download size={12} /></button>
                      <button className="mdr-act a-gray" title="Print Row" onClick={() => act('print', r)}><Printer size={12} /></button>
                      <button className="mdr-act a-gray" title="Audit Trail" onClick={() => act('audit', r)}><FileText size={12} /></button>
                    </div>
                  </td>
                  <td className="nowrap">{r.date}</td><td className="mdr-emp">{r.employee_no}</td><td className="mdr-name">{r.name}</td><td>{r.department}</td>
                  <td className="nowrap">{r.designation || '—'}</td><td>{r.grade || '—'}</td><td className="nowrap">{r.branch || '—'}</td><td className="nowrap">{r.site || '—'}</td>
                  <td><Chip tone={r.incident === 'Lateness' ? 'orange' : r.incident === 'Leave Offset' ? 'indigo' : r.incident === 'Biometric Error' ? 'teal' : 'blue'}>{r.incident}</Chip></td>
                  <td><span className="mds-wrap">{r.reason || '—'}</span></td>
                  <td><Chip tone={APPR_TONE[r.approval]}>{r.approval}</Chip></td><td className="nowrap">{r.leave_type || '—'}</td>
                  <td>{r.paid ? <span className="mds-yn yes">✓ Yes</span> : <span className="mds-yn no">No</span>}</td>
                  <td>{r.deducted ? <span className="mds-yn bad">✓ Yes</span> : <span className="mds-yn no">No</span>}</td>
                  <td className="r nowrap">{fmtMoney(r.daily_rate)}</td><td className="r nowrap">{fmtMoney(r.hourly_rate)}</td>
                  <td className="c">{num(r.missed_days)}</td><td className="c">{num(r.late_mins)}</td><td className="c">{r.late_hrs.toFixed(2)}</td>
                  <td className="r nowrap mdp-cost">{money(r.company_paid)}</td><td className="r nowrap mdp-neg">{money(r.deduction)}</td>
                  <td className="r nowrap mdp-save">{money(r.saved)}</td><td className="r nowrap mdp-op">{money(r.operational_cost)}</td>
                  <td className={`r nowrap ${r.net > 0 ? 'mdp-neg' : 'mdp-save'}`}><b>{r.net < 0 ? `-${fmtMoney(-r.net)}` : fmtMoney(r.net)}</b></td>
                  <td className="nowrap">{r.impact_type}</td><td><Chip tone={SEV_TONE[r.severity]}>{r.severity}</Chip></td>
                  <td><span className="mds-wrap">{r.hr_comment || '—'}</span></td><td><span className="mds-wrap">{r.mgmt_comment || '—'}</span></td>
                  <td><Chip tone={PAY_TONE[r.payroll]}>{r.payroll}</Chip></td><td><span className="mds-wrap">{r.action_taken || '—'}</span></td>
                  <td className="nowrap">{r.action_by || '—'}</td><td className="nowrap">{r.action_date || '—'}</td>
                  <td><Chip tone={COST_TONE[r.cost_status]}>{r.cost_status}</Chip></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={COLS.length + 2} className="mdr-empty">No incidents for the selected filters.</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr>
                <td>TOTALS</td><td className="no-print" /><td colSpan={3}>{rows.length} record(s)</td><td colSpan={16} />
                <td className="r">{fmtMoney(sum('company_paid'))}</td><td className="r">{fmtMoney(sum('deduction'))}</td><td className="r">{fmtMoney(sum('saved'))}</td>
                <td className="r">{fmtMoney(sum('operational_cost'))}</td><td className="r">{k.net < 0 ? `-${fmtMoney(-k.net)}` : fmtMoney(k.net)}</td><td colSpan={9} />
              </tr></tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="mdr-card mdp-groups">
        <div className="mds-table-head">
          <b>Cost Summary by Group</b>
          <button className="mdr-btn no-print" onClick={() => setOpen(o => !o)}>{open ? <><ChevronUp size={14} /> Collapse</> : <><ChevronDown size={14} /> Expand</>}</button>
        </div>
        {open && (
          <div className="mdp-group-list">
            <GroupTable title="Cost Summary by Department" label="Department" rows={rows} by={r => r.department} />
            <GroupTable title="Cost Summary by Grade" label="Grade" rows={rows} by={r => r.grade || 'Ungraded'} />
            <GroupTable title="Cost Summary by Designation" label="Designation" rows={rows} by={r => r.designation || 'Unassigned'} />
            <GroupTable title="Cost Summary by Employee" label="Employee" rows={rows} by={r => r.employee_id} employee />
          </div>
        )}
      </div>

      {modal?.kind === 'view' && <ViewModal r={modal.row} onClose={() => setModal(null)} />}
      {modal?.kind === 'edit' && <EditModal r={modal.row} opts={d.options} onClose={() => setModal(null)} onSave={b => save(modal.row, b).then(() => setModal(null))} />}
      {modal?.kind === 'comment' && <CommentModal r={modal.row} opts={d.options} onClose={() => setModal(null)} onSave={b => save(modal.row, { ...b, _action: 'comment' }, 'Management comment added').then(() => setModal(null))} />}
      {modal?.kind === 'attach' && <AttachModal r={modal.row} onClose={() => { setModal(null); load() }} ensure={() => save(modal.row, {}, false).then(x => x.id)} />}
      {modal?.kind === 'audit' && <AuditModal r={modal.row} onClose={() => setModal(null)} />}
    </>
  )
}

function GroupTable({ title, label, rows, by, employee }) {
  const groups = useMemo(() => {
    const m = new Map()
    for (const r of rows) {
      const key = by(r)
      const g = m.get(key) || { key, name: employee ? r.name : key, employee_no: r.employee_no, department: r.department, n: 0, paid: 0, saved: 0, op: 0, net: 0, emps: new Set() }
      g.n++; g.paid += r.company_paid; g.saved += r.saved; g.op += r.operational_cost; g.net += r.net; g.emps.add(r.employee_id)
      m.set(key, g)
    }
    return [...m.values()].sort((a, b) => b.net - a.net)
  }, [rows]) // eslint-disable-line react-hooks/exhaustive-deps
  const tot = groups.reduce((t, g) => ({ n: t.n + g.n, paid: t.paid + g.paid, saved: t.saved + g.saved, op: t.op + g.op, net: t.net + g.net }), { n: 0, paid: 0, saved: 0, op: 0, net: 0 })
  const net = v => (v < 0 ? `-${fmtMoney(-v)}` : fmtMoney(v))
  return (
    <div className="mdp-group">
      <div className="mdl-sub">{title}</div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table">
          <thead><tr>
            {employee ? <><th>Employee ID</th><th>Employee Name</th><th>Department</th></> : <th>{label}</th>}
            <th className="c">Incidents</th><th className="r">Approved Paid</th><th className="r">Salary Saved</th><th className="r">Operational Cost</th><th className="r">Net Cost</th>
            {!employee && <th className="c">Employees</th>}
          </tr></thead>
          <tbody>
            {groups.map(g => (
              <tr key={g.key} className="mdr-row">
                {employee ? <><td className="mdr-emp">{g.employee_no}</td><td className="mdr-name">{g.name}</td><td>{g.department}</td></> : <td className="mdr-name">{g.name}</td>}
                <td className="c">{g.n}</td><td className="r">{fmtMoney(g.paid)}</td><td className="r mdp-save">{fmtMoney(g.saved)}</td>
                <td className="r mdp-op">{money(g.op)}</td><td className={`r ${g.net > 0 ? 'mdp-neg' : 'mdp-save'}`}><b>{net(g.net)}</b></td>
                {!employee && <td className="c">{g.emps.size}</td>}
              </tr>
            ))}
            {!groups.length && <tr><td colSpan={8} className="mdr-empty">No incidents.</td></tr>}
          </tbody>
          {groups.length > 0 && <tfoot><tr>
            <td colSpan={employee ? 3 : 1}>TOTAL</td><td className="c">{tot.n}</td><td className="r">{fmtMoney(tot.paid)}</td><td className="r">{fmtMoney(tot.saved)}</td>
            <td className="r">{fmtMoney(tot.op)}</td><td className="r">{net(tot.net)}</td>{!employee && <td className="c">{new Set(rows.map(r => r.employee_id)).size}</td>}
          </tr></tfoot>}
        </table>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- row windows

const Field = ({ label, children }) => <div className="mdp-field"><dt>{label}</dt><dd>{children ?? '—'}</dd></div>

function ViewModal({ r, onClose }) {
  const net = r.net < 0 ? `${fmtMoney(-r.net)} (SAVING)` : `${fmtMoney(r.net)} (COST)`
  return (
    <Modal className="mdl-modal mdp-view" wide title={<><Eye size={18} /> Cost Impact Detail — {r.date} | {r.name}</>} onClose={onClose}>
      <div className="mdl-modal-body">
        <div className="mdp-view-tags">
          <Chip tone={APPR_TONE[r.approval]}>{r.approval}{r.paid ? ' / Paid' : r.deducted ? ' / Deducted' : ''}</Chip><Chip tone="blue">{r.incident}</Chip>
          <Chip tone={SEV_TONE[r.severity]}>{r.severity}</Chip><Chip tone={COST_TONE[r.cost_status]}>{r.cost_status}</Chip>
          <button className="mdr-btn no-print" onClick={() => window.print()}><Printer size={13} /> Print</button>
        </div>
        <section><h4>Employee</h4><dl>
          <Field label="Name">{r.name}</Field><Field label="Employee ID">{r.employee_no}</Field><Field label="Department">{r.department}</Field>
          <Field label="Designation">{r.designation}</Field><Field label="Grade">{r.grade}</Field><Field label="Branch">{r.branch}</Field><Field label="Project Site">{r.site}</Field>
        </dl></section>
        <section><h4>Incident Details</h4><dl>
          <Field label="Incident Date">{r.date}</Field><Field label="Incident Type">{r.incident}</Field>{r.reference && <Field label="MD ID">{r.reference}</Field>}
          <Field label="Reason">{r.reason}</Field><Field label="Approval Status">{r.approval}</Field><Field label="Leave Type Used">{r.leave_type}</Field>
          <Field label="Paid?">{r.paid ? 'Yes' : 'No'}</Field><Field label="Deducted?">{r.deducted ? 'Yes' : 'No'}</Field><Field label="Payroll Status">{r.payroll}</Field>
        </dl></section>
        <section><h4>Cost Breakdown</h4><dl>
          <Field label="Daily Rate">{fmtMoney(r.daily_rate)}</Field><Field label="Hourly Rate">{fmtMoney(r.hourly_rate)}</Field><Field label="Missed Days">{num(r.missed_days)}</Field>
          <Field label="Lateness Mins">{r.late_mins} min</Field><Field label="Approved Paid Amount">{fmtMoney(r.company_paid)}</Field><Field label="Deduction Amount">{fmtMoney(r.deduction)}</Field>
          <Field label="Salary Cost Saved">{fmtMoney(r.saved)}</Field><Field label="Operational Cost">{fmtMoney(r.operational_cost)}</Field>
          <Field label="Net Cost to Company"><b className={r.net > 0 ? 'neg' : 'pos'}>{net}</b></Field>
        </dl></section>
        <section><h4>Management Impact</h4><dl>
          <Field label="Impact Type">{r.impact_type}</Field><Field label="Impact Severity">{r.severity}</Field><Field label="Client Affected">{r.client_affected}</Field>
          <Field label="Project Affected">{r.project_affected}</Field><Field label="Business Description">{r.business_description}</Field><Field label="Business Effect">{r.business_effect}</Field>
          <Field label="HR Comment">{r.hr_comment}</Field><Field label="Management Comment">{r.mgmt_comment}</Field><Field label="Recommended Action">{r.recommended_action}</Field>
          <Field label="Final Decision">{r.final_decision}</Field><Field label="Action Taken">{r.action_taken}</Field><Field label="Action By">{r.action_by}</Field>
          <Field label="Action Date">{r.action_date}</Field><Field label="Evidence">{r.evidence ? `${r.evidence} file(s)` : null}</Field>
        </dl></section>
      </div>
    </Modal>
  )
}

function EditModal({ r, opts, onClose, onSave }) {
  const [v, setV] = useState({
    operational_cost: r.operational_cost || '', severity: r.severity, impact_type: r.impact_type, cost_status: r.cost_status,
    client_affected: r.client_affected || '', project_affected: r.project_affected || '', business_description: r.business_description || '',
    hr_comment: r.hr_comment || '', mgmt_comment: r.mgmt_comment || '', recommended_action: r.recommended_action || '', action_taken: r.action_taken || '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k, x) => setV(o => ({ ...o, [k]: x }))
  const submit = e => { e.preventDefault(); setBusy(true); onSave({ ...v, operational_cost: Number(v.operational_cost || 0) }).catch(() => {}).finally(() => setBusy(false)) }
  return (
    <Modal className="mdl-modal" wide title={<><Pen size={18} /> Edit Cost Impact — {r.name} | {r.date}</>} onClose={onClose}>
      <form className="mdl-modal-body" onSubmit={submit}>
        <div className="mdl-info"><AlertTriangle size={15} /> You may only edit cost impact and comment fields. Salary rate, payroll amount and posted deductions are locked.</div>
        <div className="mds-grid mdp-form">
          <label>Operational Cost (₦)<input type="number" min="0" step="0.01" value={v.operational_cost} onChange={e => set('operational_cost', e.target.value)} /></label>
          <label>Impact Severity<Select value={v.severity} onChange={e => set('severity', e.target.value)}>{opts.severities.map(o => <option key={o}>{o}</option>)}</Select></label>
          <label>Management Impact Type<Select value={v.impact_type} onChange={e => set('impact_type', e.target.value)}>{opts.impact_types.map(o => <option key={o}>{o}</option>)}</Select></label>
          <label>Status<Select value={v.cost_status} onChange={e => set('cost_status', e.target.value)}>{opts.statuses.map(o => <option key={o}>{o}</option>)}</Select></label>
          <label>Client Affected<input value={v.client_affected} onChange={e => set('client_affected', e.target.value)} /></label>
          <label>Project Affected<input value={v.project_affected} onChange={e => set('project_affected', e.target.value)} /></label>
          <label className="span2">Business Description<textarea rows={2} value={v.business_description} onChange={e => set('business_description', e.target.value)} /></label>
          <label className="span2">HR Comment<textarea rows={2} value={v.hr_comment} onChange={e => set('hr_comment', e.target.value)} /></label>
          <label className="span2">Management Comment<textarea rows={2} value={v.mgmt_comment} onChange={e => set('mgmt_comment', e.target.value)} /></label>
          <label>Recommended Action<Select value={v.recommended_action} onChange={e => set('recommended_action', e.target.value)}><option value="">—</option>{opts.actions.map(o => <option key={o}>{o}</option>)}</Select></label>
          <label>Action Taken<input value={v.action_taken} onChange={e => set('action_taken', e.target.value)} /></label>
        </div>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="mds-run" disabled={busy}>{busy ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </form>
    </Modal>
  )
}

function CommentModal({ r, opts, onClose, onSave }) {
  const { user } = useHR()
  const [v, setV] = useState({ impact_type: r.impact_type, severity: r.severity, mgmt_comment: r.mgmt_comment || '', business_effect: r.business_effect || '',
    final_decision: r.final_decision || '', recommended_action: r.recommended_action || '', comment_by: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '', comment_date: new Date().toISOString().slice(0, 10) })
  const [busy, setBusy] = useState(false)
  const set = (k, x) => setV(o => ({ ...o, [k]: x }))
  const submit = e => { e.preventDefault(); if (!v.mgmt_comment.trim()) return; setBusy(true); onSave(v).catch(() => {}).finally(() => setBusy(false)) }
  return (
    <Modal className="mdl-modal" wide title={<><MessageSquare size={18} /> Add Management Comment — {r.name}</>} onClose={onClose}>
      <form className="mdl-modal-body" onSubmit={submit}>
        <div className="mds-grid mdp-form">
          <label>Management Impact Type<Select value={v.impact_type} onChange={e => set('impact_type', e.target.value)}>{opts.impact_types.map(o => <option key={o}>{o}</option>)}</Select></label>
          <label>Impact Severity<Select value={v.severity} onChange={e => set('severity', e.target.value)}>{opts.severities.map(o => <option key={o}>{o}</option>)}</Select></label>
          <label className="span2">Management Comment *<textarea rows={3} required value={v.mgmt_comment} onChange={e => set('mgmt_comment', e.target.value)} /></label>
          <label className="span2">Business Effect<textarea rows={2} value={v.business_effect} onChange={e => set('business_effect', e.target.value)} /></label>
          <label className="span2">Final Decision<textarea rows={2} value={v.final_decision} onChange={e => set('final_decision', e.target.value)} /></label>
          <label>Recommended Action<Select value={v.recommended_action} onChange={e => set('recommended_action', e.target.value)}><option value="">—</option>{opts.actions.map(o => <option key={o}>{o}</option>)}</Select></label>
          <label>Comment By<input value={v.comment_by} onChange={e => set('comment_by', e.target.value)} /></label>
          <label>Comment Date<DateInput value={v.comment_date} onChange={e => set('comment_date', e.target.value)} /></label>
        </div>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="mds-run" disabled={busy || !v.mgmt_comment.trim()}>{busy ? 'Saving…' : 'Submit Comment'}</button>
        </div>
      </form>
    </Modal>
  )
}

function AttachModal({ r, onClose, ensure }) {
  const { notify } = useHR()
  const [id, setId] = useState(r.cost_id)
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (id) hr.mdEvidence(id).then(setFiles).catch(() => {}) }, [id])
  const pick = async list => {
    setBusy(true)
    try {
      const rid = id || await ensure()
      setId(rid)
      for (const file of list) await hr.mdAddEvidence(rid, file)
      setFiles(await hr.mdEvidence(rid))
      notify(`${list.length} file(s) attached`)
    } catch (e) { notify(e.message, 'bad') } finally { setBusy(false) }
  }
  const remove = f => hr.mdDeleteEvidence(f.id).then(() => setFiles(x => x.filter(y => y.id !== f.id))).catch(e => notify(e.message, 'bad'))
  return (
    <Modal className="mdl-modal" title={<><Paperclip size={18} /> Attach Evidence — {r.name} | {r.date}</>} onClose={onClose}>
      <div className="mdl-modal-body">
        <p className="mdl-modal-lead">Attach supporting documents for this incident: site logs, replacement invoices, client correspondence, medical notes.</p>
        <FilePicker rule={CHECKLIST_RULE} multiple onPick={pick} onError={m => notify(m, 'bad')} label={busy ? 'Uploading…' : 'Click to upload evidence'} />
        <div className="mdp-files">
          {files.map(f => <FileChip key={f.id} name={f.name} size={f.size} onOpen={() => openStored(() => hr.mdEvidenceBlob(f.id))} onRemove={() => remove(f)} />)}
          {!files.length && <div className="mdr-empty">No evidence attached yet.</div>}
        </div>
        <div className="mdh-form-foot"><button type="button" className="mds-run" onClick={onClose}>Done</button></div>
      </div>
    </Modal>
  )
}

function AuditModal({ r, onClose }) {
  const [rows, setRows] = useState(null)
  useEffect(() => { hr.mdCostAudit(r.source, r.source_id).then(x => setRows(x.rows)).catch(() => setRows([])) }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Modal className="mdl-modal" wide title={<><FileText size={18} /> Audit Trail — {r.name} | {r.date}</>} onClose={onClose}>
      <div className="mdl-modal-body">
        {!rows ? <Loader title="Loading…" subtitle="Fetching the record" /> : (
          <div className="mds-timeline">
            {rows.map(a => (
              <div key={a.ref} className="mdr-card mds-event">
                <div className="mds-event-head"><b>{a.action.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())} <span>— {a.module} · {a.ref}</span></b><small>{String(a.on || '').replace('T', ' ').slice(0, 16)}</small></div>
                <div className="mdh-warn-text">{a.summary}</div>
                <div className="mds-by">By: {a.user}</div>
              </div>
            ))}
            {!rows.length && <div className="mdr-card mdr-empty">No audit records yet.</div>}
          </div>
        )}
      </div>
    </Modal>
  )
}
